import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useDebouncedCallback } from 'use-debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useDelayedResizeObserver } from '../../hooks/use-delayed-resize-observer';
import { CardVariant } from '../../knowledge-smart-card/knowledge-smart-card';
import { AnimatedContainer } from '../animated/animated-container';
import { ProjectHighlightCard } from '../sub-agents/components/project-highlights/project-highlight-card';
import { RelatedPersonCard } from '../sub-agents/components/related-people/related-person-card';
import type {
	CarouselElementType,
	ParsedElementType,
	PersonElementType,
	ProjectHighlightElementType,
	SmartLinkElementType,
} from '../types';

import { SmartLinkElement } from './smartlink-element';
import type { ElementPosition } from './types';

const carouselCardWidth = 355; // card width (300px) + gap (space.200 = 16px)
const scrollWidth = (carouselCardWidth + 46) * 2;

const styles = cssMap({
	carouselStyles: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		width: '100%',
		gap: token('space.100'),
	},
	carouselStylesSingleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: token('space.negative.200'),
	},
	scrollContainerStyles: {
		overflowX: 'auto',
		overflowY: 'hidden',
		display: 'block',
		width: '100%',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	scrollContainerNoScroll: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		display: 'block',
		width: '100%',
	},
	cardContainerOneItem: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: token('space.200'),
	},
	cardContainerTwoItems: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.200'),
	},
	cardContainerThreePlusItems: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr 1fr',
		gridAutoFlow: 'column',
		gridAutoColumns: '1fr',
		gap: token('space.200'),
	},
	carouselItemStyles: {
		minWidth: '224px',
		maxWidth: '360px',
	},
	carouselItemStylesFoldable: {
		minWidth: '216px',
		maxWidth: '252px',
	},
});

export type CarouselElementProps = {
	component: CarouselElementType;
	isFoldable?: boolean;
	isStreaming?: boolean;
	position: ElementPosition;
	sectionKey?: string;
	showArrows?: boolean;
};

type CarouselItemRendererProps = {
	child: ParsedElementType;
	index: number;
	isFoldable: boolean;
	isStreaming?: boolean;
	sectionKey?: string;
};

const CarouselItemRenderer = ({
	child,
	index,
	isFoldable,
	isStreaming,
	sectionKey,
}: CarouselItemRendererProps): React.JSX.Element => {
	const isProjectHighlight = typeof child !== 'string' && child.tag === 'project-highlight-card';
	const isRelatedPeopleCard = typeof child !== 'string' && child.tag === 'people-card';
	const isSmartLink = typeof child !== 'string' && child.tag === 'smart-link';

	if (isProjectHighlight) {
		return (
			<Box
				testId="carousel-item-project-highlight-card"
				xcss={isFoldable ? styles.carouselItemStylesFoldable : styles.carouselItemStyles}
			>
				<ProjectHighlightCard component={child as ProjectHighlightElementType} />
			</Box>
		);
	}

	if (isRelatedPeopleCard) {
		return (
			<Box
				testId="carousel-item-people-card"
				xcss={isFoldable ? styles.carouselItemStylesFoldable : styles.carouselItemStyles}
			>
				<RelatedPersonCard component={child as PersonElementType} />
			</Box>
		);
	}

	if (isSmartLink) {
		return (
			<Box
				testId="carousel-item-smart-link"
				xcss={isFoldable ? styles.carouselItemStylesFoldable : styles.carouselItemStyles}
			>
				<SmartLinkElement
					component={child as SmartLinkElementType}
					variant={CardVariant.COMPACT}
					isStreaming={isStreaming}
					sectionKey={sectionKey}
					containerType="carousel"
					indexInContainer={index}
				/>
			</Box>
		);
	}

	return <Box />;
};

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem ? Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1 : false;

export const CarouselElement: React.MemoExoticComponent<({ component, position, showArrows, isFoldable, isStreaming, }: CarouselElementProps) => React.JSX.Element | null> = React.memo(
	({
		component,
		position,
		showArrows = true,
		isFoldable = false,
		isStreaming = false,
		sectionKey,
	}: CarouselElementProps): React.JSX.Element | null => {
		const scrollContainerRef = useRef<HTMLDivElement>(null);
		const [hideLeftScroll, setHideLeftScroll] = useState(true);
		const [hideRightScroll, setHideRightScroll] = useState(false);
		const [isOverflowing, setIsOverflowing] = useState(false);

		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		const key_id = useRef(uuidv4());

		const carouselChildren = (component.children || []) as ParsedElementType[];
		const shouldAlignHeader = position === 'single-column';

		const updateArrowVisibility = useCallback(() => {
			if (scrollContainerRef.current) {
				setHideLeftScroll(isScrollAtLeft(scrollContainerRef.current));
				setHideRightScroll(isScrollAtRight(scrollContainerRef.current));
			}
		}, []);

		const checkOverflow = useCallback(() => {
			if (scrollContainerRef.current) {
				const { scrollWidth: sw, clientWidth: cw } = scrollContainerRef.current;
				setIsOverflowing(sw > cw);
			}
		}, []);

		// Detect overflow changes when the scroll container is resized
		const handleResize = useCallback<ResizeObserverCallback>(() => {
			checkOverflow();
			updateArrowVisibility();
		}, [checkOverflow, updateArrowVisibility]);

		useDelayedResizeObserver(scrollContainerRef, handleResize, 100, [carouselChildren.length]);

		useEffect(() => {
			const scrollContainer = scrollContainerRef.current;
			if (scrollContainer && carouselChildren.length) {
				checkOverflow();
				updateArrowVisibility();

				const unbind = bind(scrollContainer, {
					type: 'scroll',
					listener: updateArrowVisibility,
				});

				return unbind;
			}
		}, [carouselChildren.length, checkOverflow, updateArrowVisibility]);

		const [onScroll] = useDebouncedCallback(() => {
			updateArrowVisibility();
		}, 30);

		const scrollLeft = () => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollLeft -= scrollWidth;
			}
		};

		const scrollRight = () => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollLeft += scrollWidth;
			}
		};

		if (!carouselChildren || carouselChildren.length < 2) {
			return null;
		}

		return (
			<AnimatedContainer isStreaming={isStreaming}>
				<Box
					xcss={cx(shouldAlignHeader ? styles.carouselStylesSingleRow : styles.carouselStyles)}
					testId="lumina-layout-carousel"
				>
					<Inline spread="space-between" grow="fill" alignBlock="center">
						{!shouldAlignHeader && component.attributes?.title ? (
							<Inline space="space.100" alignBlock="center">
								<Text
									weight="semibold"
									size="small"
									color="color.text.accent.gray"
									testId="carousel-title"
								>
									{component.attributes?.title}
								</Text>
							</Inline>
						) : (
							<Box />
						)}
						{showArrows && (!hideLeftScroll || !hideRightScroll) && (
							<Inline space="space.100">
								<IconButton
									icon={ArrowLeftIcon}
									label="scroll left"
									onClick={scrollLeft}
									shape="circle"
									appearance="subtle"
									spacing="compact"
									isDisabled={hideLeftScroll}
								/>
								<IconButton
									icon={ArrowRightIcon}
									label="scroll right"
									onClick={scrollRight}
									shape="circle"
									appearance="subtle"
									spacing="compact"
									isDisabled={hideRightScroll}
								/>
							</Inline>
						)}
					</Inline>
					<Box
						xcss={
							isOverflowing
								? styles.scrollContainerStyles
								: styles.scrollContainerNoScroll
						}
						ref={scrollContainerRef}
						onScroll={onScroll}
						tabIndex={isOverflowing ? 0 : undefined}
					>
						<Box
							xcss={
								carouselChildren.length === 1
									? styles.cardContainerOneItem
									: carouselChildren.length === 2
										? styles.cardContainerTwoItems
										: styles.cardContainerThreePlusItems
							}
						>
							{carouselChildren.map((child, index) => (
								<CarouselItemRenderer
									key={`${key_id.current}-${index}`}
									child={child}
									index={index}
									isFoldable={isFoldable}
									isStreaming={isStreaming}
									sectionKey={sectionKey}
								/>
							))}
						</Box>
					</Box>
				</Box>
			</AnimatedContainer>
		);
	});
